import {
  lazy,
  Suspense,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ComponentType,
  type ReactNode,
} from 'react'
import { motion, useReducedMotion } from 'motion/react'

import {
  HERO_SUN_COLOR,
  HERO_SUN_GLOW_RGB,
  HeroSunDisc,
  heroSunDiscSizeClassName,
  heroSunGlowBoxShadow,
} from '@/components/hero-sun-disc'
import { useStaticNoAnimation } from '@/context/static-no-animation'
import { usePrefersReducedMotion } from '@/hooks/use-prefers-reduced-motion'
import { prefersReducedMotion } from '@/lib/accessibility'
import { mixHexColors } from '@/lib/color'
import { cn } from '@/lib/utils'
import { canCreateWebGLContext } from '@/lib/webgl'

const HeroEtherWebGLBackground = lazy(() =>
  import('@/components/hero-ether-webgl-background').then((m) => ({
    default: m.HeroEtherWebGLBackground,
  })),
)

function scheduleIdle(callback: () => void, timeout = 2000): () => void {
  if (typeof window.requestIdleCallback === 'function') {
    const id = window.requestIdleCallback(callback, { timeout })
    return () => window.cancelIdleCallback(id)
  }
  const id = window.setTimeout(callback, 1)
  return () => window.clearTimeout(id)
}

const introRevealEase = [0.16, 1, 0.3, 1] as const

function HeroIntroReveal({ children }: { children: ReactNode }) {
  const framerReducedMotion = useReducedMotion()
  const a11yReducedMotion = usePrefersReducedMotion()
  const staticNoAnimation = useStaticNoAnimation()
  const reduced =
    Boolean(framerReducedMotion) || a11yReducedMotion || staticNoAnimation

  const className =
    'mx-auto max-w-3xl text-center text-base/relaxed font-bold text-pretty text-white/90 sm:text-lg'

  if (reduced) {
    return <div className={className}>{children}</div>
  }

  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y: 20, filter: 'blur(8px)' }}
      whileInView={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
      viewport={{ once: true, amount: 0.7, margin: '0px 0px -12% 0px' }}
      transition={{ duration: 0.9, ease: introRevealEase }}
    >
      {children}
    </motion.div>
  )
}

/**
 * Rising-sun hero with Morning’s Ether sky gradient backdrop.
 * Plays a one-shot sunrise on load — the sun starts as a sliver on the horizon
 * under a pre-dawn sky, then rises into its resting state as the sky clears.
 */

const HERO_ETHER_CLOUDS_WIDTH = 4425
const HERO_ETHER_CLOUDS_HEIGHT = 392
const HERO_ETHER_CLOUDS_PNG = '/hero-ether-clouds-landscape.png'
const HERO_ETHER_CLOUDS_AVIF_SRCSET = [
  '/hero-ether-clouds-landscape-1600.avif 1600w',
  '/hero-ether-clouds-landscape-2400.avif 2400w',
  '/hero-ether-clouds-landscape-4425.avif 4425w',
].join(', ')
const HERO_ETHER_CLOUDS_WEBP_SRCSET = [
  '/hero-ether-clouds-landscape-1600.webp 1600w',
  '/hero-ether-clouds-landscape-2400.webp 2400w',
  '/hero-ether-clouds-landscape-4425.webp 4425w',
].join(', ')
/** Marquee tiles are 150vw each — one cached request serves both tiles. */
const HERO_ETHER_CLOUDS_SIZES = '150vw'
const LINE_COUNT = 34
/** Pre-dawn dimming — sky stays visible underneath. */
const VEIL_RGB = '11, 16, 24'
const VEIL_DARK_ALPHA = 0.72
/** Ocean starts under the same pre-dawn load dim, then clears with the sun rise. */
const OCEAN_LOAD_DIM_RGB = '4, 20, 24'
const OCEAN_LOAD_DIM_ALPHA = 0.92
/** Align sun center with the sky/ocean horizon (top of the ocean band). */
const SUN_HORIZON_OFFSET = '20%'
const SUN_WRAP_TRANSFORM = 'translateX(-50%) translateY(50%)'

/** Sun wrapper translateY, in percent: pre-dawn sliver → resting half disc. */
const SUN_RISE_FROM = 92
const SUN_RISE_TO = 50
/** Dark mode rests the sun lower on the horizon (higher translateY). */
const SUN_RISE_TO_DARK = 86
/** Glow intensity across the rise (see `heroSunGlowBoxShadow`). */
const SUN_GLOW_FROM = 0.15
const SUN_GLOW_TO = 1
const SUN_GLOW_TO_DARK = 0.35
/** Residual sky/ocean dim once the sunrise settles in dark mode. */
const VEIL_REST_OPACITY_DARK = 0.55
const OCEAN_DIM_REST_OPACITY_DARK = 0.48
/** Theme toggle — sun drop + sky dim ease. */
const THEME_BLEND_MS = 900
/**
 * Pre-dawn disc — hot white-yellow core like a hazy sunrise bloom,
 * warming to full daylight orange as it rises.
 */
const SUN_DAWN_COLOR = '#fffef0'
/** Golden halo around the white-yellow core (matches pre-dawn reference). */
const SUN_DAWN_GLOW_RGB = [255, 214, 80] as const

function isDocumentDark() {
  return document.documentElement.classList.contains('dark')
}

/** Beat of stillness on the pre-dawn frame before the sun starts moving. */
const INTRO_HOLD_MS = 250
const INTRO_DURATION_MS = 2000
/** Start hero copy load-in this long before the sun settles. */
const COPY_REVEAL_LEAD_MS = 1000
/** Segment ends as a fraction of the total timeline. */
const SUN_RISE_END = 0.78
/** White-yellow → daylight orange completes early while the disc is still rising. */
const SUN_COLOR_WARM_END = 0.28
/** Sky finishes clearing just before the sun settles. */
const VEIL_CLEAR_END = 0.72
const OCEAN_CLEAR_END = 0.8

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value))
}

function easeOutCubic(t: number) {
  return 1 - (1 - t) ** 3
}

function lerp(from: number, to: number, t: number) {
  return from + (to - from) * t
}

/** Eased 0–1 sub-progress for a segment of the timeline. */
function segment(start: number, end: number, progress: number) {
  if (end <= start) return progress >= end ? 1 : 0
  return easeOutCubic(clamp((progress - start) / (end - start), 0, 1))
}

function computeLinePositions(containerHeight: number): number[] {
  const h = containerHeight || 1
  const base = 3.4
  const factor = 1.08
  let total = 0
  for (let k = 0; k < LINE_COUNT - 1; k++) total += base * factor ** k
  const fit = (h * 0.9) / total
  const out: number[] = []
  let pos = 0
  for (let i = 0; i < LINE_COUNT; i++) {
    out.push(pos)
    if (i < LINE_COUNT - 1) {
      pos += base * factor ** i * fit
    }
  }
  return out
}

function EtherCloudsPicture({ fetchPriority }: { fetchPriority?: 'high' | 'low' | 'auto' }) {
  return (
    <picture>
      <source
        type="image/avif"
        srcSet={HERO_ETHER_CLOUDS_AVIF_SRCSET}
        sizes={HERO_ETHER_CLOUDS_SIZES}
      />
      <source
        type="image/webp"
        srcSet={HERO_ETHER_CLOUDS_WEBP_SRCSET}
        sizes={HERO_ETHER_CLOUDS_SIZES}
      />
      <img
        src={HERO_ETHER_CLOUDS_PNG}
        alt=""
        aria-hidden
        width={HERO_ETHER_CLOUDS_WIDTH}
        height={HERO_ETHER_CLOUDS_HEIGHT}
        decoding="async"
        loading={fetchPriority === 'high' ? 'eager' : undefined}
        fetchPriority={fetchPriority}
        className="size-full shrink-0 object-cover object-top opacity-80"
      />
    </picture>
  )
}

function EtherCloudsMarqueeLayer({ className }: { className?: string }) {
  return (
    <div className={className}>
      <div className="hero-ether-clouds-marquee flex h-full w-[300vw]">
        {/* Same URL on both tiles — browser cache prevents a second download. */}
        <div className="hero-ether-clouds-tile flex h-full w-[150vw] shrink-0">
          <EtherCloudsPicture fetchPriority="high" />
        </div>
        <div className="hero-ether-clouds-tile flex h-full w-[150vw] shrink-0" aria-hidden>
          <EtherCloudsPicture fetchPriority="low" />
        </div>
      </div>
    </div>
  )
}

type HeroSunriseScrollSectionProps = {
  children: ReactNode
  /** Intro copy anchored inside the ocean band. */
  introText?: ReactNode
  /** Pill eyebrow anchored to the bottom edge of the hero. */
  bottomEyebrow?: ReactNode
  /** Fires when hero copy should load in — near the end of sunrise, or immediately when skipped. */
  onSunriseComplete?: () => void
  className?: string
  /** Override the sky WebGL layer — defaults to the three.js implementation. */
  skyBackground?: ComponentType<{ className?: string }>
}

export function HeroSunriseScrollSection({
  children,
  introText,
  bottomEyebrow,
  onSunriseComplete,
  className,
  skyBackground,
}: HeroSunriseScrollSectionProps) {
  const SkyBackground = skyBackground ?? HeroEtherWebGLBackground
  const rootRef = useRef<HTMLDivElement>(null)
  const heroWrapRef = useRef<HTMLDivElement>(null)
  const skyLayerRef = useRef<HTMLDivElement>(null)
  const overlayRef = useRef<HTMLDivElement>(null)
  const sunWrapRef = useRef<HTMLDivElement>(null)
  const sunRef = useRef<HTMLDivElement>(null)
  const linesRef = useRef<HTMLDivElement>(null)
  const oceanDimRef = useRef<HTMLDivElement>(null)
  const textWrapRef = useRef<HTMLDivElement>(null)
  const maskRef = useRef<HTMLDivElement>(null)
  const progressRef = useRef(1)
  const onSunriseCompleteRef = useRef(onSunriseComplete)
  onSunriseCompleteRef.current = onSunriseComplete

  const staticNoAnimation = useStaticNoAnimation()
  const a11yReducedMotion = usePrefersReducedMotion()
  const [showWebGL, setShowWebGL] = useState(false)
  /** The CSS sky covers the rise; the shader chunk waits so it can't steal frames. */
  const [sunriseSettled, setSunriseSettled] = useState(false)

  useEffect(() => {
    if (staticNoAnimation || a11yReducedMotion || prefersReducedMotion()) {
      return
    }
    if (!sunriseSettled || !canCreateWebGLContext()) return
    return scheduleIdle(() => setShowWebGL(true))
  }, [staticNoAnimation, a11yReducedMotion, sunriseSettled])

  useLayoutEffect(() => {
    const skyLayer = skyLayerRef.current
    const sun = sunRef.current
    const sunWrap = sunWrapRef.current
    const overlay = overlayRef.current
    const lines = linesRef.current
    const oceanDim = oceanDimRef.current
    const mask = maskRef.current
    const textWrap = textWrapRef.current
    if (
      !skyLayer ||
      !sun ||
      !sunWrap ||
      !overlay ||
      !lines ||
      !oceanDim ||
      !mask ||
      !textWrap
    )
      return

    const lineEls = Array.from(lines.children) as HTMLElement[]
    const dawnColor = SUN_DAWN_COLOR

    const updateMaskClip = () => {
      const sunRect = sun.getBoundingClientRect()
      const wrapRect = textWrap.getBoundingClientRect()
      const cx = sunRect.left + sunRect.width / 2 - wrapRect.left
      const cy = sunRect.top + sunRect.height / 2 - wrapRect.top
      const r = sunRect.width / 2
      mask.style.clipPath = `circle(${r}px at ${cx}px ${cy}px)`
    }

    /** 0 = light rest, 1 = dark rest — animated on theme toggle. */
    let themeBlend = isDocumentDark() ? 1 : 0
    let lastDark = isDocumentDark()
    let rafId = 0
    let holdId = 0
    let themeRafId = 0

    /** Renders the hero at a point on the load timeline; 1 is the resting state. */
    const applyFrame = (progress: number, blend = themeBlend) => {
      progressRef.current = progress

      skyLayer.style.removeProperty('mask-image')
      skyLayer.style.removeProperty('-webkit-mask-image')
      overlay.style.removeProperty('mask-image')
      overlay.style.removeProperty('-webkit-mask-image')

      const riseT = segment(0, SUN_RISE_END, progress)
      const colorWarmT = segment(0, SUN_COLOR_WARM_END, progress)
      const sunRiseTo = lerp(SUN_RISE_TO, SUN_RISE_TO_DARK, blend)
      const sunGlowTo = lerp(SUN_GLOW_TO, SUN_GLOW_TO_DARK, blend)
      const veilRest = lerp(0, VEIL_REST_OPACITY_DARK, blend)
      const oceanDimRest = lerp(0, OCEAN_DIM_REST_OPACITY_DARK, blend)
      overlay.style.opacity = `${lerp(
        1,
        veilRest,
        segment(0, VEIL_CLEAR_END, progress),
      )}`
      oceanDim.style.opacity = `${lerp(
        1,
        oceanDimRest,
        segment(0, OCEAN_CLEAR_END, progress),
      )}`
      sunWrap.style.transform = `translateX(-50%) translateY(${lerp(
        SUN_RISE_FROM,
        sunRiseTo,
        riseT,
      )}%)`
      // White core → yellow limb, collapsing to solid daylight orange.
      const dawnCore = mixHexColors('#ffffff', HERO_SUN_COLOR, colorWarmT)
      const dawnMid = mixHexColors(dawnColor, HERO_SUN_COLOR, colorWarmT)
      const dawnRim = mixHexColors('#ffd650', HERO_SUN_COLOR, colorWarmT)
      if (colorWarmT >= 0.999) {
        sun.style.backgroundImage = 'none'
        sun.style.backgroundColor = HERO_SUN_COLOR
      } else {
        sun.style.backgroundColor = dawnRim
        sun.style.backgroundImage = `radial-gradient(circle at 50% 42%, ${dawnCore} 0%, ${dawnMid} 38%, ${dawnRim} 100%)`
      }
      const glowRgb: [number, number, number] = [
        Math.round(
          lerp(SUN_DAWN_GLOW_RGB[0], HERO_SUN_GLOW_RGB[0], colorWarmT),
        ),
        Math.round(
          lerp(SUN_DAWN_GLOW_RGB[1], HERO_SUN_GLOW_RGB[1], colorWarmT),
        ),
        Math.round(
          lerp(SUN_DAWN_GLOW_RGB[2], HERO_SUN_GLOW_RGB[2], colorWarmT),
        ),
      ]
      sun.style.boxShadow = heroSunGlowBoxShadow(
        lerp(SUN_GLOW_FROM, sunGlowTo, colorWarmT),
        glowRgb,
      )
      lines.style.opacity = '1'
      // Copy entrance is owned by HeroLandingContent after sunrise completes.
      textWrap.style.opacity = '1'

      const linePos = computeLinePositions(lines.offsetHeight)
      lineEls.forEach((el, i) => {
        el.style.transform = `translateY(${linePos[i] ?? 0}px)`
      })

      // The sun moves during the rise, so the clipped copy must re-track it.
      updateMaskClip()
    }

    const animateThemeBlend = (toDark: boolean) => {
      const target = toDark ? 1 : 0
      if (themeRafId) {
        window.cancelAnimationFrame(themeRafId)
        themeRafId = 0
      }

      if (
        staticNoAnimation ||
        prefersReducedMotion() ||
        Math.abs(themeBlend - target) < 0.001
      ) {
        themeBlend = target
        applyFrame(progressRef.current)
        return
      }

      const from = themeBlend
      const start = performance.now()
      const tick = (now: number) => {
        const t = easeOutCubic(clamp((now - start) / THEME_BLEND_MS, 0, 1))
        themeBlend = lerp(from, target, t)
        applyFrame(progressRef.current)
        if (t < 1) {
          themeRafId = window.requestAnimationFrame(tick)
          return
        }
        themeRafId = 0
        themeBlend = target
      }
      themeRafId = window.requestAnimationFrame(tick)
    }

    const notifyComplete = () => {
      onSunriseCompleteRef.current?.()
    }

    if (staticNoAnimation || prefersReducedMotion()) {
      applyFrame(1)
      notifyComplete()
    } else {
      applyFrame(0)
      // Hold the pre-dawn frame, then rise.
      holdId = window.setTimeout(() => {
        holdId = 0
        const start = performance.now()
        const copyRevealAt = Math.max(0, INTRO_DURATION_MS - COPY_REVEAL_LEAD_MS)
        let copyNotified = false
        const tick = (now: number) => {
          const elapsed = now - start
          const t = clamp(elapsed / INTRO_DURATION_MS, 0, 1)
          applyFrame(t)
          if (!copyNotified && elapsed >= copyRevealAt) {
            copyNotified = true
            notifyComplete()
          }
          if (t < 1) {
            rafId = window.requestAnimationFrame(tick)
            return
          }
          rafId = 0
          if (!copyNotified) notifyComplete()
          setSunriseSettled(true)
        }
        rafId = window.requestAnimationFrame(tick)
      }, INTRO_HOLD_MS)
    }

    const onResize = () => {
      applyFrame(progressRef.current)
    }
    window.addEventListener('resize', onResize)

    // Ease sun height + residual dim when theme toggles after sunrise.
    const themeObserver = new MutationObserver(() => {
      const dark = isDocumentDark()
      if (dark === lastDark) return
      lastDark = dark
      animateThemeBlend(dark)
    })
    themeObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    })

    return () => {
      if (holdId) window.clearTimeout(holdId)
      if (rafId) window.cancelAnimationFrame(rafId)
      if (themeRafId) window.cancelAnimationFrame(themeRafId)
      window.removeEventListener('resize', onResize)
      themeObserver.disconnect()
    }
  }, [staticNoAnimation])

  return (
    <section
      ref={rootRef}
      className={cn(
        'relative z-20 -mt-22 sm:-mt-25',
        className,
      )}
    >
      <div
        ref={heroWrapRef}
        className="relative isolate min-h-[650px] h-[calc(100svh+5.5rem)] w-full overflow-clip bg-ocean-deep sm:h-[calc(100svh+6.25rem)]"
      >
        {/* Sky + clouds — always visible. */}
        <div
          ref={skyLayerRef}
          className="pointer-events-none absolute inset-x-0 top-0 z-0 h-[80%]"
        >
          <div
            aria-hidden
            className="hero-ether-sky-fallback pointer-events-none absolute inset-0"
          />
          {showWebGL ? (
            <Suspense fallback={null}>
              <SkyBackground className="hero-ether-sky-fade-in pointer-events-none absolute inset-0" />
            </Suspense>
          ) : null}
          <EtherCloudsMarqueeLayer
            className={cn(
              'pointer-events-none absolute inset-x-0 top-0 z-[1] h-[min(70%,620px)] w-full select-none overflow-hidden opacity-70 transition-opacity duration-1000 ease-out dark:opacity-35',
              'mask-[linear-gradient(to_bottom,black_72%,transparent)] [-webkit-mask-image:linear-gradient(to_bottom,black_72%,transparent)]',
            )}
          />
        </div>

        {/* Horizon-line stack — dark morning ocean band beneath the rising sun. */}
        <div
          id="theOcean"
          className="pointer-events-none absolute inset-x-0 bottom-0 z-4 h-[20%] overflow-hidden"
        >
          <div
            aria-hidden
            className="hero-ocean-gradient pointer-events-none absolute inset-[-12%]"
          />
          <div
            aria-hidden
            className="hero-ocean-shimmer pointer-events-none absolute inset-[-14%]"
          />
          <div
            aria-hidden
            className="hero-ocean-grain pointer-events-none absolute inset-0"
          />
          <div
            ref={oceanDimRef}
            aria-hidden
            className="pointer-events-none absolute inset-0 z-0 opacity-0"
            style={{
              backgroundColor: `rgba(${OCEAN_LOAD_DIM_RGB}, ${OCEAN_LOAD_DIM_ALPHA})`,
            }}
          />
          <div
            ref={linesRef}
            className={cn(
              'relative z-1 size-full',
              'mask-[linear-gradient(to_bottom,black,rgba(0,0,0,0.5))]',
              '[-webkit-mask-image:linear-gradient(to_bottom,black,rgba(0,0,0,0.5))]',
            )}
          >
            {Array.from({ length: LINE_COUNT }, (_, i) => (
              <span
                key={i}
                className="absolute top-0 left-0 block h-px w-full bg-zinc-900/55 dark:bg-black/55"
              />
            ))}
          </div>
          <div
            aria-hidden
            className="hero-ocean-fade-to-deep pointer-events-none absolute inset-0 z-2"
          />
          {introText ? (
            <div className="absolute inset-x-0 bottom-4 z-3 px-6 sm:bottom-8">
              <HeroIntroReveal>{introText}</HeroIntroReveal>
            </div>
          ) : null}
        </div>

        {/* The sun — centered on the horizon as a half disc above the ocean band. */}
        <div
          id="theSun"
          ref={sunWrapRef}
          aria-hidden
          className={cn(
            'pointer-events-none absolute left-1/2 z-3',
            heroSunDiscSizeClassName,
          )}
          style={{
            bottom: SUN_HORIZON_OFFSET,
            transform: SUN_WRAP_TRANSFORM,
          }}
        >
          <HeroSunDisc ref={sunRef} />
        </div>

        {/* Pre-dawn veil — hidden in static state. */}
        <div
          ref={overlayRef}
          aria-hidden
          className="pointer-events-none absolute inset-0 z-2 opacity-0"
          style={{
            backgroundColor: `rgba(${VEIL_RGB}, ${VEIL_DARK_ALPHA})`,
          }}
        />

        {/* Hero content — centered in the top sky / gradient band (4/5 height). */}
        <div className="absolute inset-x-0 top-0 z-10 mx-auto flex h-[80%] max-w-6xl items-center justify-center px-6">
          <div ref={textWrapRef} className="relative w-full">
            {children}
            {/* Inverted copy, clipped to the sun’s disc — darkens headline where the sun passes. */}
            <div
              ref={maskRef}
              aria-hidden
              inert
              className="pointer-events-none absolute inset-0 z-19 **:text-[#0b1018]!"
              style={{ clipPath: 'circle(0px at 50% 50%)' }}
            >
              {children}
            </div>
          </div>
        </div>

      </div>

      {bottomEyebrow ? (
        <div className="pointer-events-none absolute inset-x-0 bottom-0 z-9 flex translate-y-1/2 justify-center px-6">
          <div className="pointer-events-auto">{bottomEyebrow}</div>
        </div>
      ) : null}
    </section>
  )
}
